import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Image as ImageIcon, X, Star, TrendingUp } from 'lucide-react';
import { apiUrl, adminFetch } from '../../api';

const MAX_SIGNATURE = 3;
const MAX_TOP = 3;

const MAX_IMAGES = 5;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyProduct = {
  productName: '',
  tagline: '',
  productDesc: '',
  benefits: '',
  ingredients: '',
  variants: [{ weight: '', price: '' }],
  isSoldOut: false,
  images: [],
};

function AdminProducts() {
  const [categories, setCategories] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [products, setProducts] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', subtitle: '', collectionTitle: '', description: '' });
  const [form, setForm] = useState(emptyProduct);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [flags, setFlags] = useState({ signatureCount: 0, topByCategory: {} });

  const loadFlags = useCallback(async () => {
    try {
      const res = await adminFetch(apiUrl('/api/admin/flag-counts'));
      const data = await res.json();
      if (data.success) setFlags({ signatureCount: data.signatureCount, topByCategory: data.topByCategory });
    } catch { /* ignore */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await adminFetch(apiUrl('/api/admin/categories'));
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories);
        setActiveId((prev) => prev || (data.categories[0] && data.categories[0]._id) || null);
      }
    } catch {
      setError('Could not load categories. Is the server running?');
    }
  }, []);

  const loadProducts = useCallback(async (id) => {
    if (!id) { setProducts([]); return; }
    try {
      const res = await adminFetch(apiUrl(`/api/admin/categories/${id}/products`));
      const data = await res.json();
      if (data.success) setProducts(data.products);
    } catch {
      setError('Could not load products.');
    }
  }, []);

  useEffect(() => { loadCategories(); loadFlags(); }, [loadCategories, loadFlags]);
  useEffect(() => { loadProducts(activeId); }, [activeId, loadProducts]);

  const toggleFlag = async (product, kind) => {
    const path = kind === 'signature' ? 'signature' : 'top';
    const value = kind === 'signature' ? !product.isSignature : !product.isTopProduct;
    setError('');
    try {
      const res = await adminFetch(apiUrl(`/api/admin/products/${product._id}/${path}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }
      await loadProducts(activeId);
      await loadFlags();
    } catch {
      setError('Could not update flag.');
    }
  };

  /* ---- Categories ---- */
  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    setBusy(true); setError('');
    try {
      const res = await adminFetch(apiUrl('/api/admin/categories'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCategory),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }
      setNewCategory({ name: '', subtitle: '', collectionTitle: '', description: '' });
      await loadCategories();
      setActiveId(data.category._id);
    } finally { setBusy(false); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category and all its products?')) return;
    await adminFetch(apiUrl(`/api/admin/categories/${id}`), { method: 'DELETE' });
    if (activeId === id) setActiveId(null);
    await loadCategories();
  };

  /* ---- Product form helpers ---- */
  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    const room = MAX_IMAGES - form.images.length;
    if (room <= 0) { setError(`You can upload at most ${MAX_IMAGES} images.`); return; }
    const encoded = await Promise.all(files.slice(0, room).map(fileToBase64));
    setForm((f) => ({ ...f, images: [...f.images, ...encoded] }));
    e.target.value = '';
  };
  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const setVariant = (idx, key, val) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === idx ? { ...v, [key]: val } : v)) }));
  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { weight: '', price: '' }] }));
  const removeVariant = (idx) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  /* ---- Products ---- */
  const addProduct = async (e) => {
    e.preventDefault();
    const ingredients = form.ingredients.split(',').map((t) => t.trim()).filter(Boolean);
    const benefits = form.benefits.split(',').map((t) => t.trim()).filter(Boolean);
    const variants = form.variants.filter((v) => v.weight.trim() && v.price !== '');
    // Require every field before posting
    if (!activeId) { setError('Select a category first.'); return; }
    if (!form.productName.trim()) { setError('Product name is required.'); return; }
    if (!form.tagline.trim()) { setError('Tagline is required.'); return; }
    if (!form.productDesc.trim()) { setError('Description is required.'); return; }
    if (ingredients.length === 0) { setError('Add at least one ingredient.'); return; }
    if (benefits.length === 0) { setError('Add at least one benefit.'); return; }
    if (variants.length === 0) { setError('Add at least one variant with weight and price.'); return; }
    if (form.images.length === 0) { setError('Add at least one photo.'); return; }
    setBusy(true); setError('');
    try {
      const payload = {
        categoryId: activeId,
        productName: form.productName,
        tagline: form.tagline,
        productDesc: form.productDesc,
        benefits,
        ingredients,
        variants,
        isSoldOut: form.isSoldOut,
        images: form.images,
      };
      const res = await adminFetch(apiUrl('/api/admin/products'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }
      setForm(emptyProduct);
      setShowForm(false);
      await loadProducts(activeId);
      await loadCategories();
    } finally { setBusy(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await adminFetch(apiUrl(`/api/admin/products/${id}`), { method: 'DELETE' });
    await loadProducts(activeId);
    await loadCategories();
    await loadFlags();
  };

  const activeCategory = categories.find((c) => c._id === activeId);

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <form className="admin-add-collection" onSubmit={addCategory}>
          <input
            className="admin-input sm"
            placeholder="Category / section (e.g. Snacks)"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
          />
          <input
            className="admin-input sm"
            placeholder="Subtitle - 3 words at the top (e.g. Fresh · Handcrafted · Wellness)"
            value={newCategory.subtitle}
            onChange={(e) => setNewCategory({ ...newCategory, subtitle: e.target.value })}
          />
          <input
            className="admin-input sm"
            placeholder="Collection title (e.g. The Crunch Collection)"
            value={newCategory.collectionTitle}
            onChange={(e) => setNewCategory({ ...newCategory, collectionTitle: e.target.value })}
          />
          <input
            className="admin-input sm"
            placeholder="Description - paragraph below the title"
            value={newCategory.description}
            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
          />
          <button className="admin-btn-primary sm" type="submit" disabled={busy}>
            <Plus size={15} /> Add Section
          </button>
        </form>

        <div className="admin-collection-list">
          {categories.length === 0 && <p className="admin-muted">No sections yet.</p>}
          {categories.map((c) => (
            <div
              key={c._id}
              className={`admin-collection-item ${activeId === c._id ? 'active' : ''}`}
              onClick={() => setActiveId(c._id)}
            >
              <div className="admin-collection-meta">
                <span className="admin-collection-name">{c.Name}</span>
                <span className="admin-collection-count">{c.productCount} product{c.productCount === 1 ? '' : 's'}</span>
              </div>
              <button
                className="admin-icon-btn"
                title="Delete section"
                onClick={(e) => { e.stopPropagation(); deleteCategory(c._id); }}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-main-header">
          <div>
            <h2>{activeCategory ? activeCategory.Name : 'Products'}</h2>
            <p className="admin-muted">
              {activeCategory ? (activeCategory.Description || 'Manage products in this section') : 'Select or create a section to begin'}
            </p>
            {activeCategory && (
              <div className="admin-flag-status">
                <span><Star size={13} /> Signature {flags.signatureCount}/{MAX_SIGNATURE}</span>
                <span><TrendingUp size={13} /> Top in {activeCategory.Name} {(flags.topByCategory[activeId] || 0)}/{MAX_TOP}</span>
              </div>
            )}
          </div>
          {activeCategory && (
            <button className="admin-btn-primary" onClick={() => setShowForm((s) => !s)}>
              {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add Product</>}
            </button>
          )}
        </header>

        {error && <div className="admin-error">{error}</div>}

        {activeCategory && showForm && (
          <form className="admin-listing-form" onSubmit={addProduct}>
            <div className="admin-form-grid">
              <div className="admin-field span2">
                <label className="admin-label">Product name *</label>
                <input className="admin-input" value={form.productName}
                  onChange={(e) => setForm({ ...form, productName: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Tagline *</label>
                <input className="admin-input" placeholder="The hug your gut's been waiting for" value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Description *</label>
                <textarea className="admin-input" rows={3} value={form.productDesc}
                  onChange={(e) => setForm({ ...form, productDesc: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Ingredients (comma separated) *</label>
                <input className="admin-input" placeholder="Cardamom, Black tea, Jaggery" value={form.ingredients}
                  onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Benefits (comma separated) *</label>
                <input className="admin-input" placeholder="Gut-friendly, Gluten-free, High protein" value={form.benefits}
                  onChange={(e) => setForm({ ...form, benefits: e.target.value })} />
              </div>
            </div>

            <div className="admin-field">
              <label className="admin-label">Variants (weight &amp; price) *</label>
              {form.variants.map((v, i) => (
                <div className="admin-variant-row" key={i}>
                  <input className="admin-input" placeholder="Weight (e.g. 250g)" value={v.weight}
                    onChange={(e) => setVariant(i, 'weight', e.target.value)} />
                  <input className="admin-input" type="number" min="0" placeholder="Price" value={v.price}
                    onChange={(e) => setVariant(i, 'price', e.target.value)} />
                  {form.variants.length > 1 && (
                    <button type="button" className="admin-icon-btn danger" onClick={() => removeVariant(i)}>
                      <X size={15} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="admin-btn-ghost dark sm" onClick={addVariant}>
                <Plus size={14} /> Add variant
              </button>
            </div>

            <label className="admin-checkbox">
              <input type="checkbox" checked={form.isSoldOut}
                onChange={(e) => setForm({ ...form, isSoldOut: e.target.checked })} />
              Mark as sold out
            </label>

            <div className="admin-field">
              <label className="admin-label">Photos ({form.images.length}/{MAX_IMAGES}) *</label>
              <div className="admin-image-row">
                {form.images.map((src, i) => (
                  <div className="admin-thumb" key={i}>
                    <img src={src} alt={`upload ${i + 1}`} />
                    <button type="button" className="admin-thumb-x" onClick={() => removeImage(i)}><X size={12} /></button>
                  </div>
                ))}
                {form.images.length < MAX_IMAGES && (
                  <label className="admin-upload-box">
                    <ImageIcon size={18} />
                    <span>Add</span>
                    <input type="file" accept="image/*" multiple hidden onChange={onPickImages} />
                  </label>
                )}
              </div>
            </div>

            <button className="admin-btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save Product'}
            </button>
          </form>
        )}

        {activeCategory && (
          <div className="admin-listing-grid">
            {products.length === 0 && <p className="admin-muted">No products yet. Add one above.</p>}
            {products.map((p) => (
              <div className="admin-listing-card" key={p._id}>
                <div className="admin-listing-thumb">
                  {p.Images && p.Images[0]
                    ? <img src={p.Images[0]} alt={p.productName} />
                    : <div className="admin-listing-noimg"><ImageIcon size={22} /></div>}
                  {p.Images && p.Images.length > 1 && (
                    <span className="admin-listing-count">+{p.Images.length - 1}</span>
                  )}
                  {p.isSoldOut && <span className="admin-soldout-badge">Sold out</span>}
                </div>
                <div className="admin-listing-body">
                  <h4>{p.productName}</h4>
                  {p.variants && p.variants.length > 0 && (
                    <p className="admin-muted sm">
                      {p.variants.map((v) => `${v.weight} · £${v.price}`).join('  |  ')}
                    </p>
                  )}
                  {p.ProductDesc && <p className="admin-listing-desc">{p.ProductDesc}</p>}
                  {p.Ingredients && p.Ingredients.length > 0 && (
                    <div className="admin-tag-row">{p.Ingredients.slice(0, 5).map((t, i) => <span key={i} className="admin-tag">{t}</span>)}</div>
                  )}
                  <div className="admin-flag-toggles">
                    <button
                      type="button"
                      className={`admin-flag-toggle ${p.isSignature ? 'on' : ''}`}
                      onClick={() => toggleFlag(p, 'signature')}
                      title="Show in homepage Signature Favourites (max 3)"
                    >
                      <Star size={13} fill={p.isSignature ? 'currentColor' : 'none'} /> Signature
                    </button>
                    <button
                      type="button"
                      className={`admin-flag-toggle ${p.isTopProduct ? 'on' : ''}`}
                      onClick={() => toggleFlag(p, 'top')}
                      title="Show in homepage Top 3 for this category (max 3)"
                    >
                      <TrendingUp size={13} /> Top in category
                    </button>
                  </div>
                </div>
                <button className="admin-icon-btn danger" onClick={() => deleteProduct(p._id)} title="Delete product">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminProducts;
